import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Cloud, CloudOff, RefreshCw, Check, AlertTriangle, Smartphone, Laptop, Clock, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import useOfflineSync from '@/hooks/useOfflineSync';
import { getPendingMutations, pullChanges } from '@/services/offlineQueue';


export default function DataSync() {
  const { user } = useOutletContext();
  const { t } = useTranslation();
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [autoSync, setAutoSync] = useState(true);
  const [devices, setDevices] = useState([]);
  const [syncProgress, setSyncProgress] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const { online, pendingCount, flushing, flushNow } = useOfflineSync();

  useEffect(() => {
    fetchSyncStatus();
    fetchDevices();
    refreshPending();
  }, []);

  useEffect(() => {
    refreshPending();
  }, [pendingCount]);

  const refreshPending = async () => {
    const items = await getPendingMutations();
    setPendingItems(items);
  };

  const handleFlushPending = async () => {
    if (!online) {
      toast.error('You are offline — cannot sync now');
      return;
    }
    const result = await flushNow();
    toast.success(`Synced ${result.applied} mutations${result.errors?.length ? ` (${result.errors.length} errors)` : ''}`);
    await refreshPending();
  };

  const handlePullChanges = async () => {
    try {
      setSyncing(true);
      const data = await pullChanges();
      const totals = Object.entries(data.collections || {}).map(([k, v]) => `${k}:${v.length}`).join(', ');
      toast.success(`Pulled changes — ${totals || 'no updates'}`);
      fetchSyncStatus();
    } catch (e) {
      toast.error('Pull failed: ' + e.message);
    } finally {
      setSyncing(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await fetch(`${API}/sync/status`, { credentials: 'include' });
      if (response.ok) {
        setSyncStatus(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await fetch(`${API}/sync/devices`, { credentials: 'include' });
      if (response.ok) {
        setDevices(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setSyncProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${API}/sync/now`, {
        method: 'POST',
        credentials: 'include'
      });

      clearInterval(progressInterval);
      setSyncProgress(100);

      if (response.ok) {
        const result = await response.json();
        toast.success(`Sync complete! ${result.items_synced || 0} items synchronized.`);
        fetchSyncStatus();
        fetchDevices();
      } else {
        toast.error('Sync failed. Please try again.');
      }
    } catch (error) {
      toast.error('Sync failed. Please check your connection.');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncProgress(0), 1000);
    }
  };

  const handleAutoSyncToggle = async () => {
    const newValue = !autoSync;
    setAutoSync(newValue);
    
    try {
      await fetch(`${API}/sync/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ auto_sync: newValue })
      });
      toast.success(newValue ? 'Auto-sync enabled' : 'Auto-sync disabled');
    } catch (error) {
      setAutoSync(!newValue);
      toast.error('Failed to update settings');
    }
  };

  const handleForceUpload = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/sync/upload`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Uploaded ${result.items_uploaded || 0} items to cloud.`);
        fetchSyncStatus();
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleForceDownload = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/sync/download`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Downloaded ${result.items_downloaded || 0} items from cloud.`);
        fetchSyncStatus();
      }
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setSyncing(false);
    }
  };

  const getDeviceIcon = (type) => {
    return type === 'mobile' ? Smartphone : Laptop;
  };

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto" data-testid="data-sync-page">
      <div className="flex items-center gap-3 mb-8">
        <Cloud className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">{t('settings.dataSync')}</h1>
          <p className="text-muted-foreground">Keep your data synchronized across all devices</p>
        </div>
      </div>

      {/* Offline Queue (new) */}
      <Card className="p-6 glass-card mb-6" data-testid="offline-queue-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {online ? <Cloud className="h-5 w-5 text-emerald-500" /> : <CloudOff className="h-5 w-5 text-amber-500" />}
              Offline Queue
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {online
                ? `You're online. ${pendingCount} change${pendingCount === 1 ? '' : 's'} waiting to upload.`
                : `Offline — ${pendingCount} change${pendingCount === 1 ? '' : 's'} queued. Will auto-sync when you reconnect.`}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handlePullChanges} disabled={!online || syncing} data-testid="pull-changes-btn">
              <Download className="h-4 w-4 mr-2" />
              Pull
            </Button>
            <Button onClick={handleFlushPending} disabled={!online || flushing || pendingCount === 0} data-testid="flush-queue-btn">
              {flushing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {flushing ? 'Syncing…' : 'Push Now'}
            </Button>
          </div>
        </div>

        {pendingItems.length > 0 && (
          <details className="rounded-lg bg-secondary/40 p-3">
            <summary className="cursor-pointer text-sm font-medium">View {pendingItems.length} pending item{pendingItems.length === 1 ? '' : 's'}</summary>
            <ul className="mt-3 space-y-2 text-xs max-h-64 overflow-y-auto">
              {pendingItems.map((m) => (
                <li key={m.mutation_id} className="border-l-2 border-primary/40 pl-3" data-testid="pending-mutation-item">
                  <div className="font-mono text-[11px] text-muted-foreground">{m.type}</div>
                  <div className="text-muted-foreground">
                    queued {new Date(m.created_at).toLocaleString()} · attempts: {m.attempts || 0}
                    {m.last_error ? ` · error: ${m.last_error}` : ''}
                  </div>
                </li>
              ))}
            </ul>
          </details>
        )}
      </Card>

      {/* Sync Status Card */}
      <Card className="p-6 glass-card mb-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${syncStatus?.is_synced ? 'bg-success/10' : 'bg-warning/10'}`}>
              {syncStatus?.is_synced ? (
                <Check className="h-6 w-6 text-success" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-warning" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold">
                {syncStatus?.is_synced ? 'All Synced' : 'Sync Available'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {syncStatus?.last_sync ? (
                  <>
                    {t('settings.lastSynced')}: {new Date(syncStatus.last_sync).toLocaleString()}
                  </>
                ) : (
                  'Never synced'
                )}
              </p>
            </div>
          </div>
          <Button onClick={handleSync} disabled={syncing}>
            {syncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('settings.syncNow')}
              </>
            )}
          </Button>
        </div>

        {syncing && syncProgress > 0 && (
          <div className="mb-4">
            <Progress value={syncProgress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1 text-center">{syncProgress}%</p>
          </div>
        )}

        {/* Sync Stats */}
        {syncStatus && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-3 rounded-lg bg-secondary/50">
              <p className="text-2xl font-bold text-primary">{syncStatus.dose_logs || 0}</p>
              <p className="text-xs text-muted-foreground">Dose Logs</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-secondary/50">
              <p className="text-2xl font-bold text-primary">{syncStatus.vials || 0}</p>
              <p className="text-xs text-muted-foreground">Vials</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-secondary/50">
              <p className="text-2xl font-bold text-primary">{syncStatus.schedules || 0}</p>
              <p className="text-xs text-muted-foreground">Schedules</p>
            </div>
            <div className="text-center p-3 rounded-lg bg-secondary/50">
              <p className="text-2xl font-bold text-primary">{syncStatus.protocols || 0}</p>
              <p className="text-xs text-muted-foreground">Protocols</p>
            </div>
          </div>
        )}
      </Card>

      {/* Auto Sync Settings */}
      <Card className="p-6 glass-card mb-6">
        <h2 className="text-xl font-semibold mb-4">Sync Settings</h2>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('settings.autoSync')}</p>
              <p className="text-sm text-muted-foreground">
                Automatically sync data when you make changes
              </p>
            </div>
            <Switch checked={autoSync} onCheckedChange={handleAutoSyncToggle} />
          </div>

          <div className="border-t border-border pt-6">
            <p className="font-medium mb-3">Manual Sync Options</p>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={handleForceUpload} disabled={syncing}>
                <Upload className="h-4 w-4 mr-2" />
                Force Upload
              </Button>
              <Button variant="outline" onClick={handleForceDownload} disabled={syncing}>
                <Download className="h-4 w-4 mr-2" />
                Force Download
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Connected Devices */}
      <Card className="p-6 glass-card">
        <h2 className="text-xl font-semibold mb-4">Connected Devices</h2>
        
        {devices.length === 0 ? (
          <div className="text-center py-8">
            <CloudOff className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">No other devices connected</p>
            <p className="text-xs text-muted-foreground mt-1">
              Log in on another device to sync your data
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {devices.map((device, index) => {
              const Icon = getDeviceIcon(device.type);
              return (
                <div
                  key={device.device_id || index}
                  className="flex items-center justify-between p-4 rounded-lg bg-secondary/50"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">{device.name || 'Unknown Device'}</p>
                      <p className="text-xs text-muted-foreground">
                        {device.is_current ? 'This device' : `Last active: ${new Date(device.last_active).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  {device.is_current && (
                    <span className="px-2 py-1 rounded text-xs bg-primary/20 text-primary">
                      Current
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
